import { useEffect, useRef } from 'react'
import { useEditor, activeLevel } from '@/state/store'
import type { Opening, Prop, Space, SpaceKindId, StyleId, SurfaceId, WallMode } from '@/model/types'
import { SPACE_KINDS, SPACE_KIND_BY_ID, SURFACES } from '@/model/materials'
import { COMPOUND_WALL_DEFAULTS } from '@/model/defaults'
import { ParamControls } from '@/components/ParamControls'
import { PROP_FAMILIES, OPENING_FAMILIES } from '@/components/families/catalog'
import { area, bounds, rectPath } from '@/lib/geometry'
import { formatFeet } from '@/lib/units'

const WALL_MODES: { id: WallMode; label: string }[] = [
  { id: 'full', label: 'Full walls' },
  { id: 'half', label: 'Waist walls' },
  { id: 'none', label: 'No walls' },
]

export function Inspector() {
  const selection = useEditor((s) => s.selection)
  if (selection?.kind === 'spaces') {
    return selection.ids.length === 1 ? (
      <SpaceInspector spaceId={selection.ids[0]} />
    ) : (
      <BulkInspector ids={selection.ids} />
    )
  }
  if (selection?.kind === 'opening') {
    return <OpeningInspector ownerId={selection.ownerId} openingId={selection.id} />
  }
  if (selection?.kind === 'prop') {
    return <PropInspector propId={selection.id} />
  }
  return <ProjectInspector />
}

// ---------------------------------------------------------------------------

function useSpaceMutator(spaceId: string) {
  const update = useEditor((s) => s.update)
  const level = useEditor(activeLevel)
  return (fn: (sp: Space) => void, transient = false) =>
    update((d) => {
      const lvl = d.levels.find((l) => l.id === level.id)
      const sp = lvl?.spaces.find((s) => s.id === spaceId)
      if (sp) fn(sp)
    }, { transient })
}

function SpaceInspector({ spaceId }: { spaceId: string }) {
  const level = useEditor(activeLevel)
  const update = useEditor((s) => s.update)
  const select = useEditor((s) => s.select)
  const toggleSpaceEdgeWall = useEditor((s) => s.toggleSpaceEdgeWall)
  const nameFocusId = useEditor((s) => s.nameFocusId)
  const setNameFocus = useEditor((s) => s.setNameFocus)
  const nameRef = useRef<HTMLInputElement>(null)
  const space = level.spaces.find((s) => s.id === spaceId)
  const mutate = useSpaceMutator(spaceId)

  // Jump focus to the name field right after a space is drawn.
  useEffect(() => {
    if (nameFocusId === spaceId && nameRef.current) {
      nameRef.current.focus()
      nameRef.current.select()
      setNameFocus(null)
    }
  }, [nameFocusId, spaceId, setNameFocus])

  if (!space) return <ProjectInspector />

  const bb = bounds(space.polygon)
  const openEdges = new Set(space.openEdges ?? [])

  function applyKind(kind: SpaceKindId) {
    const preset = SPACE_KIND_BY_ID[kind]
    mutate((sp) => {
      sp.kind = kind
      sp.wallMode = preset.wallMode
      sp.hasCeiling = preset.hasCeiling
      sp.surface = preset.defaultSurface
    })
  }

  return (
    <section className="panel inspector">
      <div className="panel-head">
        <h3>{SPACE_KIND_BY_ID[space.kind].label}</h3>
      </div>

      <Field label="Name">
        <input
          ref={nameRef}
          className="txt"
          value={space.name}
          onChange={(e) => mutate((sp) => void (sp.name = e.target.value), true)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === 'Escape') (e.target as HTMLInputElement).blur()
          }}
        />
      </Field>

      <Field label="Kind">
        <select className="num" value={space.kind} onChange={(e) => applyKind(e.target.value as SpaceKindId)}>
          {SPACE_KINDS.map((k) => (
            <option key={k.id} value={k.id}>{k.label}</option>
          ))}
        </select>
      </Field>

      <div className="field-row">
        <Field label="Walls">
          <select className="num" value={space.wallMode} onChange={(e) => mutate((sp) => void (sp.wallMode = e.target.value as WallMode))}>
            {WALL_MODES.map((w) => (
              <option key={w.id} value={w.id}>{w.label}</option>
            ))}
          </select>
        </Field>
        <Field label="Ceiling">
          <select className="num" value={space.hasCeiling ? 'yes' : 'no'} onChange={(e) => mutate((sp) => void (sp.hasCeiling = e.target.value === 'yes'))}>
            <option value="yes">Yes</option>
            <option value="no">Open top</option>
          </select>
        </Field>
      </div>

      <Field label="Surface">
        <SurfaceSelect value={space.surface} onChange={(v) => mutate((sp) => void (sp.surface = v))} />
      </Field>

      <Field label="Floor offset (ft)" hint="− sunken, + raised">
        <input className="num" type="number" step={0.5} value={space.floorOffset} onChange={(e) => mutate((sp) => void (sp.floorOffset = Number(e.target.value)))} />
      </Field>

      {space.wallMode !== 'none' && space.polygon.length >= 3 && (
        <Field label="Walls per edge" hint="click to open / close a side">
          <div className="edge-walls">
            {space.polygon.map((_, i) => {
              const open = openEdges.has(i)
              return (
                <button
                  key={i}
                  type="button"
                  className={`edge-wall-btn${open ? ' open' : ''}`}
                  title={open ? `Edge ${i + 1}: open` : `Edge ${i + 1}: walled`}
                  onClick={() => toggleSpaceEdgeWall(space.id, i)}
                >
                  {i + 1}
                </button>
              )
            })}
          </div>
        </Field>
      )}

      <div className="field-row">
        <ReadOnly label="Width" value={formatFeet(bb.maxX - bb.minX)} />
        <ReadOnly label="Depth" value={formatFeet(bb.maxY - bb.minY)} />
      </div>
      <ReadOnly label="Area" value={`${area(space.polygon).toFixed(1)} sq ft`} />

      <button
        className="danger-btn"
        onClick={() => {
          update((d) => {
            const lvl = d.levels.find((l) => l.id === level.id)
            if (lvl) lvl.spaces = lvl.spaces.filter((s) => s.id !== spaceId)
          })
          select(null)
        }}
      >
        Delete space
      </button>
    </section>
  )
}

// ---------------------------------------------------------------------------

function BulkInspector({ ids }: { ids: string[] }) {
  const level = useEditor(activeLevel)
  const update = useEditor((s) => s.update)
  const select = useEditor((s) => s.select)
  const idSet = new Set(ids)
  const spaces = level.spaces.filter((s) => idSet.has(s.id))
  const totalArea = spaces.reduce((sum, s) => sum + area(s.polygon), 0)

  // Shared value or "mixed"
  const sharedOffset = spaces.every((s) => s.floorOffset === spaces[0]?.floorOffset) ? spaces[0]?.floorOffset : undefined

  function mutateAll(fn: (sp: Space) => void) {
    update((d) => {
      const lvl = d.levels.find((l) => l.id === level.id)
      if (!lvl) return
      for (const sp of lvl.spaces) if (idSet.has(sp.id)) fn(sp)
    })
  }

  return (
    <section className="panel inspector">
      <div className="panel-head">
        <h3>{ids.length} spaces</h3>
      </div>
      <ReadOnly label="Total area" value={`${totalArea.toFixed(0)} sq ft`} />

      <Field label="Floor offset (ft)" hint="applies to all selected">
        <input
          className="num"
          type="number"
          step={0.5}
          placeholder={sharedOffset === undefined ? 'mixed' : undefined}
          value={sharedOffset === undefined ? '' : sharedOffset}
          onChange={(e) => {
            const v = Number(e.target.value)
            if (!Number.isNaN(v)) mutateAll((sp) => void (sp.floorOffset = v))
          }}
        />
      </Field>

      <div className="bulk-nudge">
        <button className="ghost-btn" onClick={() => mutateAll((sp) => void (sp.floorOffset -= 0.5))}>− 6″</button>
        <button className="ghost-btn" onClick={() => mutateAll((sp) => void (sp.floorOffset += 0.5))}>+ 6″</button>
        <button className="ghost-btn" onClick={() => mutateAll((sp) => void (sp.floorOffset = 0))}>Reset</button>
      </div>

      <Field label="Set surface (all)">
        <SurfaceSelect value={spaces[0]?.surface ?? 'tile'} onChange={(v) => mutateAll((sp) => void (sp.surface = v))} />
      </Field>

      <Field label="Set walls (all)">
        <select className="num" defaultValue="" onChange={(e) => { if (e.target.value) mutateAll((sp) => void (sp.wallMode = e.target.value as WallMode)) }}>
          <option value="">—</option>
          {WALL_MODES.map((w) => (
            <option key={w.id} value={w.id}>{w.label}</option>
          ))}
        </select>
      </Field>

      <p className="hint-note">Shift-click a space to add/remove it. ⌘A selects all on this floor.</p>

      <button
        className="danger-btn"
        onClick={() => {
          update((d) => {
            const lvl = d.levels.find((l) => l.id === level.id)
            if (lvl) lvl.spaces = lvl.spaces.filter((s) => !idSet.has(s.id))
          })
          select(null)
        }}
      >
        Delete {ids.length} spaces
      </button>
    </section>
  )
}

// ---------------------------------------------------------------------------

function OpeningInspector({ ownerId, openingId }: { ownerId: string; openingId: string }) {
  const level = useEditor(activeLevel)
  const plot = useEditor((s) => s.project.plot)
  const update = useEditor((s) => s.update)
  const select = useEditor((s) => s.select)

  const ownerSpace = ownerId === 'compound' ? null : level.spaces.find((s) => s.id === ownerId)
  const opening: Opening | undefined =
    ownerId === 'compound'
      ? plot.compoundWall?.openings.find((o) => o.id === openingId)
      : ownerSpace?.openings.find((o) => o.id === openingId)
  const ownerLabel = ownerId === 'compound' ? 'Compound wall' : ownerSpace?.name ?? '—'

  function mutate(fn: (op: Opening) => void, transient = false) {
    update((d) => {
      const op =
        ownerId === 'compound'
          ? d.plot.compoundWall?.openings.find((o) => o.id === openingId)
          : d.levels.flatMap((l) => l.spaces).find((s) => s.id === ownerId)?.openings.find((o) => o.id === openingId)
      if (op) fn(op)
    }, { transient })
  }

  function remove() {
    update((d) => {
      if (ownerId === 'compound') {
        const cw = d.plot.compoundWall
        if (cw) cw.openings = cw.openings.filter((o) => o.id !== openingId)
      } else {
        const sp = d.levels.flatMap((l) => l.spaces).find((s) => s.id === ownerId)
        if (sp) sp.openings = sp.openings.filter((o) => o.id !== openingId)
      }
    })
    select(null)
  }

  if (!opening) return <ProjectInspector />

  const family = OPENING_FAMILIES[opening.familyId]
  const kindLabel = opening.familyId === 'gate' ? 'Gate' : opening.familyId === 'window' ? 'Window' : 'Door'

  return (
    <section className="panel inspector">
      <div className="panel-head">
        <h3>{kindLabel}</h3>
      </div>

      <ReadOnly label="On" value={ownerLabel} />

      {family && (
        <ParamControls
          schema={family.params}
          params={opening.params}
          onChange={(key, val) => mutate((op) => void (op.params[key] = val))}
        />
      )}

      <p className="hint-note">Drag the symbol on the canvas to slide it along the wall.</p>

      <button className="danger-btn" onClick={remove}>Delete {kindLabel.toLowerCase()}</button>
    </section>
  )
}

// ---------------------------------------------------------------------------

function PropInspector({ propId }: { propId: string }) {
  const level = useEditor(activeLevel)
  const update = useEditor((s) => s.update)
  const select = useEditor((s) => s.select)
  const prop = level.props.find((p) => p.id === propId)

  function mutate(fn: (p: Prop) => void, transient = false) {
    update((d) => {
      const p = d.levels.flatMap((l) => l.props).find((x) => x.id === propId)
      if (p) fn(p)
    }, { transient })
  }

  function remove() {
    update((d) => {
      for (const lvl of d.levels) lvl.props = lvl.props.filter((p) => p.id !== propId)
    })
    select(null)
  }

  if (!prop) return <ProjectInspector />

  const family = PROP_FAMILIES[prop.familyId]

  return (
    <section className="panel inspector">
      <div className="panel-head">
        <h3>{family?.label ?? 'Prop'}</h3>
      </div>

      <Field label="Label">
        <input
          className="txt"
          value={prop.label ?? ''}
          onChange={(e) => mutate((p) => void (p.label = e.target.value), true)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === 'Escape') (e.target as HTMLInputElement).blur()
          }}
        />
      </Field>

      {family && (
        <ParamControls
          schema={family.params}
          params={prop.params}
          onChange={(key, val) => mutate((p) => void (p.params[key] = val))}
        />
      )}

      <Field label="Rotation (°)" hint="[ / ] = ±15°">
        <input className="num" type="number" step={15} value={Math.round(prop.rotation)}
          onChange={(e) => mutate((p) => void (p.rotation = ((Number(e.target.value) % 360) + 360) % 360))} />
      </Field>

      <p className="hint-note">Drag the prop to move it; drag the gold handle (or [ / ]) to rotate.</p>

      <button className="danger-btn" onClick={remove}>Delete {family?.label ?? 'prop'}</button>
    </section>
  )
}

// ---------------------------------------------------------------------------

function ProjectInspector() {
  const update = useEditor((s) => s.update)
  const plot = useEditor((s) => s.project.plot)
  const settings = useEditor((s) => s.project.settings)
  const standards = settings.standards
  const level = useEditor(activeLevel)

  const b = bounds(plot.boundary)
  const plotW = b.maxX - b.minX
  const plotD = b.maxY - b.minY

  function setPlotSize(w: number, d: number) {
    update((draft) => {
      draft.plot.boundary = rectPath(0, 0, Math.max(5, w), Math.max(5, d))
    })
  }

  return (
    <section className="panel inspector">
      <div className="panel-head">
        <h3>Plot &amp; Building</h3>
      </div>

      <div className="field-row">
        <Field label="Plot width (ft)">
          <input className="num" type="number" value={plotW} onChange={(e) => setPlotSize(Number(e.target.value), plotD)} />
        </Field>
        <Field label="Plot depth (ft)">
          <input className="num" type="number" value={plotD} onChange={(e) => setPlotSize(plotW, Number(e.target.value))} />
        </Field>
      </div>

      <h4 className="sub">Setbacks (ft)</h4>
      <div className="field-row">
        <Field label="Front">
          <input className="num" type="number" value={plot.setbacks.front} onChange={(e) => update((d) => void (d.plot.setbacks.front = Number(e.target.value)))} />
        </Field>
        <Field label="Rear">
          <input className="num" type="number" value={plot.setbacks.rear} onChange={(e) => update((d) => void (d.plot.setbacks.rear = Number(e.target.value)))} />
        </Field>
      </div>
      <div className="field-row">
        <Field label="Left">
          <input className="num" type="number" value={plot.setbacks.left} onChange={(e) => update((d) => void (d.plot.setbacks.left = Number(e.target.value)))} />
        </Field>
        <Field label="Right">
          <input className="num" type="number" value={plot.setbacks.right} onChange={(e) => update((d) => void (d.plot.setbacks.right = Number(e.target.value)))} />
        </Field>
      </div>

      <h4 className="sub">Visual style</h4>
      <Field label="Style">
        <select className="num" value={settings.style ?? 'standard'} onChange={(e) => update((d) => void (d.settings.style = e.target.value as StyleId))}>
          <option value="budget">Budget</option>
          <option value="standard">Standard</option>
          <option value="premium">Premium</option>
        </select>
      </Field>

      <h4 className="sub">Standards</h4>
      <Field label="Floor height (ft)">
        <input
          className="num"
          type="number"
          step={0.5}
          value={standards.floorHeight}
          onChange={(e) => {
            const v = Number(e.target.value)
            update((d) => {
              d.settings.standards.floorHeight = v
              const lvl = d.levels.find((l) => l.id === level.id)
              if (lvl) lvl.height = v
            })
          }}
        />
      </Field>
      <div className="field-row">
        <ReadOnly label="Ext wall" value={`${(standards.exteriorWallThickness * 12).toFixed(1)} in`} />
        <ReadOnly label="Int wall" value={`${(standards.interiorWallThickness * 12).toFixed(1)} in`} />
      </div>

      <CompoundWallSettings />

      <p className="hint-note">Select a space to edit it, or shift-click / ⌘A to edit many at once. Walls auto-generate from spaces.</p>
    </section>
  )
}

function CompoundWallSettings() {
  const update = useEditor((s) => s.update)
  const cw = useEditor((s) => s.project.plot.compoundWall)
  const enabled = cw?.enabled ?? false

  function toggle(on: boolean) {
    update((d) => {
      if (on) {
        d.plot.compoundWall = d.plot.compoundWall
          ? { ...d.plot.compoundWall, enabled: true }
          : { enabled: true, ...COMPOUND_WALL_DEFAULTS, openings: [] }
      } else if (d.plot.compoundWall) {
        d.plot.compoundWall.enabled = false
      }
    })
  }

  function set(fn: (w: NonNullable<typeof cw>) => void) {
    update((d) => {
      if (d.plot.compoundWall) fn(d.plot.compoundWall)
    })
  }

  return (
    <>
      <h4 className="sub">Compound wall</h4>
      <label className="field" style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
        <input type="checkbox" checked={enabled} onChange={(e) => toggle(e.target.checked)} />
        <span className="field-label" style={{ margin: 0 }}>Wall around the plot boundary</span>
      </label>
      {enabled && cw && (
        <>
          <div className="field-row">
            <Field label="Thickness (in)">
              <input className="num" type="number" step={1} value={Math.round(cw.thickness * 12)}
                onChange={(e) => set((w) => void (w.thickness = Math.max(2, Number(e.target.value)) / 12))} />
            </Field>
            <Field label="Height (ft)">
              <input className="num" type="number" step={0.5} value={cw.height}
                onChange={(e) => set((w) => void (w.height = Math.max(1, Number(e.target.value))))} />
            </Field>
          </div>
          <Field label="Inset (ft)" hint="from plot boundary">
            <input className="num" type="number" step={0.5} min={0} value={cw.inset}
              onChange={(e) => set((w) => void (w.inset = Math.max(0, Number(e.target.value))))} />
          </Field>
          <p className="hint-note">Place a front gate with the Gate tool (Place opening → Front gate).</p>
        </>
      )}
    </>
  )
}

// ---------------------------------------------------------------------------

function SurfaceSelect({ value, onChange }: { value: SurfaceId; onChange: (v: SurfaceId) => void }) {
  const indoor = SURFACES.filter((s) => s.category === 'indoor')
  const outdoor = SURFACES.filter((s) => s.category === 'outdoor')
  return (
    <select className="num" value={value} onChange={(e) => onChange(e.target.value as SurfaceId)}>
      <optgroup label="Indoor">
        {indoor.map((s) => (
          <option key={s.id} value={s.id}>{s.label}</option>
        ))}
      </optgroup>
      <optgroup label="Outdoor">
        {outdoor.map((s) => (
          <option key={s.id} value={s.id}>{s.label}</option>
        ))}
      </optgroup>
    </select>
  )
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label className="field">
      <span className="field-label">
        {label}
        {hint && <em className="field-hint"> · {hint}</em>}
      </span>
      {children}
    </label>
  )
}

function ReadOnly({ label, value }: { label: string; value: string }) {
  return (
    <div className="field readonly">
      <span className="field-label">{label}</span>
      <span className="field-value">{value}</span>
    </div>
  )
}
